/* ==========================================================================
   POS reports on the report kit: shift close, day close, item / category /
   group / brand summary and the bill summary. Every date is the POS
   transaction date (the business date the bill was saved on), and each report
   loads only the period picked on it.
   ========================================================================== */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import toast from "react-hot-toast";
import { ReportScreen } from "../../../report";
import { dateOf, dmy, groupSum, intg, money, num, sumBy } from "../../../report/reportFormat";
import {
  getPosBills,
  getPosDayCloses,
  getPosShiftCloses,
  posError,
} from "../../../../services/PosApiServices";

const OPEN_FROM = "2000-01-01";

/** Rows for the report's period: reloads when the period or the branch changes. */
const usePeriodRows = (branch, fetcher, what) => {
  const [rows, setRows] = useState([]);
  const range = useRef(null);
  const lastKey = useRef("");

  const load = useCallback(
    async (from, to, force = false) => {
      range.current = [from, to];
      if (!branch?.guid) return;
      const f = from || OPEN_FROM;
      const t = to || dayjs().add(1, "year").format("YYYY-MM-DD");
      const key = `${branch.guid}|${f}|${t}`;
      if (!force && key === lastKey.current) return;
      lastKey.current = key;
      try {
        const data = await fetcher(branch.guid, f, t);
        if (lastKey.current === key) setRows(Array.isArray(data) ? data : []);
      } catch (error) {
        toast.error(posError(error, `Could not load the ${what}`));
      }
    },
    [branch, fetcher, what]
  );

  useEffect(() => {
    if (range.current) load(...range.current);
  }, [load]);

  const refresh = useCallback(() => range.current && load(...range.current, true), [load]);
  return { rows, load, refresh };
};

const counterFilter = (rows) => {
  const seen = new Map();
  rows.forEach((r) => r.counterID && seen.set(r.counterID, r.counterName));
  return {
    key: "counters",
    label: "Counters",
    items: [...seen].map(([v, t]) => ({ v, t: t || "Counter" })),
    match: (r, v) => v.includes(r.counterID),
  };
};

const tenderColumns = [
  { k: "cash", t: "Cash", type: "money" },
  { k: "card", t: "Card", type: "money" },
  { k: "upi", t: "UPI", type: "money" },
  { k: "credit", t: "Credit", type: "money" },
];

const closeTotalsDrawer = (r) => [
  { l: "Sales", v: money(r.salesTotal) },
  { l: `Returns (${r.returnCount})`, v: r.returnTotal ? "−" + money(r.returnTotal) : "0.00" },
  { l: "Cash", v: money(r.cash) },
  { l: "Card", v: money(r.card) },
  { l: "UPI", v: money(r.upi) },
  { l: "Credit", v: money(r.credit) },
  { l: "Expected cash", v: money(r.expectedCash) },
  { l: "Counted cash", v: money(r.countedCash) },
  { l: "Difference", v: money(r.cashDifference) },
];

/* ------------------------------------------------------------------ shift close */

export const PosShiftCloseReport = ({ branch }) => {
  const { rows: data, load, refresh } = usePeriodRows(branch, getPosShiftCloses, "shift closes");

  const rows = useMemo(
    () =>
      data.map((s) => ({
        ...s,
        key: s.guid,
        date: dateOf(s.transactionDate),
        shift: `Shift ${s.shiftNo}`,
        closedAt: s.closedDate ? dayjs(s.closedDate).format("DD MMM, hh:mm A") : "",
        net: num(s.netTotal),
      })),
    [data]
  );

  const filters = useMemo(() => [counterFilter(rows)], [rows]);

  const tabs = useMemo(
    () => [
      {
        id: "shifts",
        label: "Shift closes",
        defaultSort: { k: "date", d: -1 },
        rowKey: (r) => r.key,
        groupOptions: [
          { v: "counterName", t: "Counter" },
          { v: "__month", t: "Month" },
        ],
        columns: [
          { k: "sl", t: "Sl No", type: "index" },
          { k: "date", t: "Date", type: "date" },
          { k: "counterName", t: "Counter" },
          { k: "shiftNo", t: "Shift", type: "int", noSum: true },
          { k: "billCount", t: "Bills", type: "int" },
          { k: "returnCount", t: "Returns", type: "int" },
          { k: "salesTotal", t: "Sales", type: "money" },
          { k: "returnTotal", t: "Return Amt", type: "money" },
          { k: "discountTotal", t: "Discount", type: "money", off: true },
          { k: "taxTotal", t: "Tax", type: "money", off: true },
          { k: "netTotal", t: "Net", type: "money" },
          ...tenderColumns,
          { k: "expectedCash", t: "Expected Cash", type: "money" },
          { k: "countedCash", t: "Counted Cash", type: "money" },
          { k: "cashDifference", t: "Difference", type: "money" },
          { k: "closedUserName", t: "Closed By" },
          { k: "closedAt", t: "Closed At", off: true },
        ],
      },
    ],
    []
  );

  const kpis = useMemo(
    () => [
      { label: "Shifts", value: (l) => l.length, format: "int" },
      { label: "Bills", value: (l) => sumBy(l, "billCount"), format: "int" },
      { label: "Net sales", value: (l) => sumBy(l, "netTotal"), hero: true },
      { label: "Cash", value: (l) => sumBy(l, "cash") },
      { label: "Cash difference", value: (l) => sumBy(l, "cashDifference") },
    ],
    []
  );

  const drawerContent = (r) => ({
    title: `${r.counterName} · Shift ${r.shiftNo}`,
    subtitle: dmy(r.date),
    avatarKey: r.counterName,
    meta: [
      { k: "Bills", v: r.billCount },
      { k: "First bill", v: r.firstBillAt ? dayjs(r.firstBillAt).format("hh:mm A") : "—" },
      { k: "Last bill", v: r.lastBillAt ? dayjs(r.lastBillAt).format("hh:mm A") : "—" },
      { k: "Closed by", v: r.closedUserName || "—" },
      { k: "Closed at", v: r.closedAt },
      { k: "Remarks", v: r.remarks || "—" },
    ],
    totals: closeTotalsDrawer(r),
    net: { l: "Net", v: money(r.netTotal) },
  });

  return (
    <ReportScreen
      title="POS Shift Close Report"
      subtitle="Every closed shift, per counter, with the cash count"
      unit="shifts"
      rows={rows}
      tabs={tabs}
      filters={filters}
      kpis={kpis}
      chart={{ valueKey: "net", title: "Net sales by shift date" }}
      searchMatch={(r, q) => `${r.counterName} ${r.closedUserName} ${r.remarks ?? ""}`.toLowerCase().includes(q)}
      searchPlaceholder="Counter, closed by, remarks…"
      exportName="POS shift close"
      reportNames={{ shifts: "POS Shift Close Report" }}
      onRefresh={refresh}
      onPeriodChange={load}
      drawerContent={drawerContent}
      defaultPreset="7d"
    />
  );
};

PosShiftCloseReport.propTypes = { branch: PropTypes.object };

/* ------------------------------------------------------------------ day close */

export const PosDayCloseReport = ({ branch }) => {
  const { rows: data, load, refresh } = usePeriodRows(branch, getPosDayCloses, "day closes");

  const rows = useMemo(
    () =>
      data.map((d) => ({
        ...d,
        key: d.guid,
        date: dateOf(d.transactionDate),
        closedAt: d.closedDate ? dayjs(d.closedDate).format("DD MMM, hh:mm A") : "",
        net: num(d.netTotal),
      })),
    [data]
  );

  const tabs = useMemo(
    () => [
      {
        id: "days",
        label: "Day closes",
        defaultSort: { k: "date", d: -1 },
        rowKey: (r) => r.key,
        groupOptions: [{ v: "__month", t: "Month" }],
        columns: [
          { k: "sl", t: "Sl No", type: "index" },
          { k: "date", t: "Date", type: "date" },
          { k: "shiftCount", t: "Shifts", type: "int" },
          { k: "billCount", t: "Bills", type: "int" },
          { k: "returnCount", t: "Returns", type: "int" },
          { k: "grossTotal", t: "Gross", type: "money", off: true },
          { k: "salesTotal", t: "Sales", type: "money" },
          { k: "returnTotal", t: "Return Amt", type: "money" },
          { k: "discountTotal", t: "Discount", type: "money", off: true },
          { k: "taxTotal", t: "Tax", type: "money" },
          { k: "netTotal", t: "Net", type: "money" },
          ...tenderColumns,
          { k: "expectedCash", t: "Expected Cash", type: "money", off: true },
          { k: "countedCash", t: "Counted Cash", type: "money", off: true },
          { k: "cashDifference", t: "Difference", type: "money" },
          { k: "closedUserName", t: "Closed By" },
          { k: "closedAt", t: "Closed At", off: true },
        ],
      },
    ],
    []
  );

  const kpis = useMemo(
    () => [
      { label: "Days", value: (l) => l.length, format: "int" },
      { label: "Bills", value: (l) => sumBy(l, "billCount"), format: "int" },
      { label: "Net sales", value: (l) => sumBy(l, "netTotal"), hero: true },
      { label: "Tax", value: (l) => sumBy(l, "taxTotal") },
      { label: "Cash difference", value: (l) => sumBy(l, "cashDifference") },
    ],
    []
  );

  const drawerContent = (r) => ({
    title: `Day close ${dmy(r.date)}`,
    subtitle: `${r.shiftCount} shift(s) · next date ${dayjs(r.nextDate).format("DD MMM YYYY")}`,
    avatarKey: r.date,
    meta: [
      { k: "Bills", v: r.billCount },
      { k: "Closed by", v: r.closedUserName || "—" },
      { k: "Closed at", v: r.closedAt },
      { k: "Remarks", v: r.remarks || "—" },
    ],
    totals: closeTotalsDrawer(r),
    net: { l: "Net", v: money(r.netTotal) },
  });

  return (
    <ReportScreen
      title="POS Day Close Report"
      subtitle="Every closed business date with its totals"
      unit="days"
      rows={rows}
      tabs={tabs}
      filters={[]}
      kpis={kpis}
      chart={{ valueKey: "net", title: "Net sales by day" }}
      searchMatch={(r, q) => `${r.closedUserName} ${r.remarks ?? ""} ${r.date}`.toLowerCase().includes(q)}
      searchPlaceholder="Closed by, remarks, date…"
      exportName="POS day close"
      reportNames={{ days: "POS Day Close Report" }}
      onRefresh={refresh}
      onPeriodChange={load}
      drawerContent={drawerContent}
      defaultPreset="30d"
    />
  );
};

PosDayCloseReport.propTypes = { branch: PropTypes.object };

/* ------------------------------------------------------------------ bills (shared) */

/** One row per POS bill or return; a return's figures are negative. */
const useBillRows = (branch) => {
  const { rows: data, load, refresh } = usePeriodRows(branch, getPosBills, "POS bills");
  const rows = useMemo(
    () =>
      data.map((b) => {
        const sign = b.transType === 3 ? -1 : 1;
        return {
          key: b.guid,
          guid: b.guid,
          date: dateOf(b.transactionDate),
          time: b.invDate ? dayjs(b.invDate).format("hh:mm A") : "",
          billNo: b.entryNumber,
          type: sign < 0 ? "Return" : "Bill",
          sign,
          refNo: b.refNo || "",
          customer: b.customerName || "Walk-in",
          counterID: b.counterID,
          counterName: b.counterName,
          shiftNo: b.shiftNo,
          cashier: b.cashierName || "",
          gross: sign * num(b.grossTotal),
          lineDisc: sign * num(b.lineDiscount),
          billDisc: sign * num(b.billDiscount),
          addition: sign * num(b.additionalAmt),
          tax: sign * num(b.taxTotal),
          roundOff: sign * num(b.roundOff),
          net: sign * num(b.netTotal),
          lines: b.lines || [],
        };
      }),
    [data]
  );
  return { rows, load, refresh };
};

const typeFilter = {
  key: "types",
  label: "Type",
  items: [
    { v: "Bill", t: "Bills" },
    { v: "Return", t: "Returns" },
  ],
  match: (r, v) => v.includes(r.type),
};

const billDrawer = (r) => ({
  title: r.customer,
  subtitle: `${r.billNo} · ${dmy(r.date)} ${r.time}`,
  avatarKey: r.customer,
  meta: [
    { k: "Type", v: r.refNo ? `${r.type} of ${r.refNo}` : r.type },
    { k: "Counter", v: `${r.counterName} · shift ${r.shiftNo ?? "—"}` },
    { k: "Cashier", v: r.cashier || "—" },
  ],
  lines: {
    title: "Lines",
    columns: [{ t: "Item" }, { t: "Qty", align: "right" }, { t: "Rate", align: "right" }, { t: "Amount", align: "right" }],
    rows: r.lines.map((l) => [
      <>
        <div className="axr-nm">{l.productName || "Item"}</div>
        <div className="axr-cd">{[l.productCode, l.unitName].filter(Boolean).join(" · ")}</div>
      </>,
      intg(l.quantity),
      money(l.price),
      money(l.netAmt),
    ]),
  },
  totals: [
    { l: "Gross", v: money(r.gross) },
    { l: "Line discount", v: money(r.lineDisc) },
    { l: "Bill discount", v: money(r.billDisc) },
    { l: "Addition", v: money(r.addition) },
    { l: "Tax", v: money(r.tax) },
    { l: "Round off", v: money(r.roundOff) },
  ],
  net: { l: "Net", v: money(r.net) },
});

const billSearch = (r, q) =>
  `${r.billNo} ${r.customer} ${r.refNo} ${r.counterName}`.toLowerCase().includes(q) ||
  r.lines.some((l) => `${l.productName} ${l.productCode}`.toLowerCase().includes(q));

/* ------------------------------------------------------------------ bill summary */

export const PosBillSummary = ({ branch }) => {
  const { rows, load, refresh } = useBillRows(branch);
  const filters = useMemo(() => [typeFilter, counterFilter(rows)], [rows]);

  const tabs = useMemo(
    () => [
      {
        id: "bills",
        label: "Bill summary",
        defaultSort: { k: "date", d: -1 },
        rowKey: (r) => r.key,
        groupOptions: [
          { v: "customer", t: "Customer" },
          { v: "counterName", t: "Counter" },
          { v: "type", t: "Type" },
          { v: "__month", t: "Month" },
        ],
        columns: [
          { k: "sl", t: "Sl No", type: "index" },
          { k: "date", t: "Date", type: "date" },
          { k: "billNo", t: "Invoice No", type: "code" },
          { k: "customer", t: "Customer", type: "avatar" },
          { k: "type", t: "Type", off: true },
          { k: "counterName", t: "Counter", off: true },
          { k: "shiftNo", t: "Shift", type: "int", noSum: true, off: true },
          { k: "gross", t: "Gross", type: "money" },
          { k: "lineDisc", t: "Line Disc", type: "money" },
          { k: "billDisc", t: "Bill Disc", type: "money" },
          { k: "addition", t: "Addition", type: "money" },
          { k: "tax", t: "Tax Amt", type: "money" },
          { k: "roundOff", t: "Round Off", type: "money" },
          { k: "net", t: "Net Amt", type: "money" },
        ],
      },
    ],
    []
  );

  const kpis = useMemo(
    () => [
      { label: "Bills", value: (l) => l.filter((r) => r.sign > 0).length, format: "int" },
      { label: "Returns", value: (l) => l.filter((r) => r.sign < 0).length, format: "int" },
      { label: "Discount", value: (l) => sumBy(l, "lineDisc") + sumBy(l, "billDisc"), invert: true },
      { label: "Tax", value: (l) => sumBy(l, "tax") },
      { label: "Net", value: (l) => sumBy(l, "net"), hero: true },
    ],
    []
  );

  return (
    <ReportScreen
      title="POS Bill Summary"
      subtitle="Every POS bill and return; returns count negative"
      unit="bills"
      rows={rows}
      tabs={tabs}
      filters={filters}
      kpis={kpis}
      chart={{ valueKey: "net", title: "Net POS sales" }}
      searchMatch={billSearch}
      searchPlaceholder="Invoice no, customer, item…"
      exportName="POS bill summary"
      reportNames={{ bills: "POS Bill Summary" }}
      onRefresh={refresh}
      onPeriodChange={load}
      drawerContent={billDrawer}
    />
  );
};

PosBillSummary.propTypes = { branch: PropTypes.object };

/* ------------------------------------------------------------------ item / category / group / brand */

const SUMMARY_FIELDS = ["qty", "gross", "discount", "tax", "net"];

/** Lines of the listed bills summed by keyFn; returned lines subtract. */
const summarise = (list, keyFn, labelFn) => {
  const lines = [];
  list.forEach((r) =>
    r.lines.forEach((l) =>
      lines.push({
        group: keyFn(l),
        label: labelFn(l),
        qty: r.sign * num(l.quantity),
        gross: r.sign * num(l.gross),
        discount: r.sign * num(l.discAmt),
        tax: r.sign * num(l.taxAmt),
        net: r.sign * num(l.netAmt),
      })
    )
  );
  return groupSum(lines, (x) => x.group, SUMMARY_FIELDS, (x) => x.label);
};

const summaryColumns = (first) => [
  ...first,
  { k: "n", t: "Lines", type: "int", off: true },
  { k: "qty", t: "Qty", type: "int" },
  { k: "gross", t: "Gross", type: "money" },
  { k: "discount", t: "Discount", type: "money" },
  { k: "tax", t: "Tax", type: "money" },
  { k: "net", t: "Net", type: "money" },
  { k: "share", t: "Share of Net", type: "share", shareOf: "net" },
];

export const PosSummaryReport = ({ branch }) => {
  const { rows, load, refresh } = useBillRows(branch);
  const filters = useMemo(() => [counterFilter(rows)], [rows]);

  const tabs = useMemo(
    () => [
      {
        id: "item",
        label: "Item summary",
        defaultSort: { k: "net", d: -1 },
        rowKey: (r) => r.key,
        drillDown: false,
        rows: (list) =>
          summarise(
            list,
            (l) => l.productID || l.productName,
            (l) => ({ code: l.productCode || "—", name: l.productName || "Item", category: l.categoryName || "—" })
          ),
        columns: summaryColumns([
          { k: "code", t: "Item Code", type: "code" },
          { k: "name", t: "Item Name" },
          { k: "category", t: "Category", off: true },
        ]),
      },
      {
        id: "category",
        label: "Category summary",
        defaultSort: { k: "net", d: -1 },
        rowKey: (r) => r.key,
        drillDown: false,
        rows: (list) => summarise(list, (l) => l.categoryID || "none", (l) => ({ name: l.categoryName || "No category" })),
        columns: summaryColumns([{ k: "name", t: "Category", type: "avatar" }]),
      },
      {
        id: "group",
        label: "Group summary",
        defaultSort: { k: "net", d: -1 },
        rowKey: (r) => r.key,
        drillDown: false,
        rows: (list) => summarise(list, (l) => l.groupID || "none", (l) => ({ name: l.groupName || "No group" })),
        columns: summaryColumns([{ k: "name", t: "Group", type: "avatar" }]),
      },
      {
        id: "brand",
        label: "Brand summary",
        defaultSort: { k: "net", d: -1 },
        rowKey: (r) => r.key,
        drillDown: false,
        rows: (list) => summarise(list, (l) => l.brandID || "none", (l) => ({ name: l.brandName || "No brand" })),
        columns: summaryColumns([{ k: "name", t: "Brand", type: "avatar" }]),
      },
    ],
    []
  );

  const kpis = useMemo(
    () => [
      { label: "Bills", value: (l) => l.filter((r) => r.sign > 0).length, format: "int" },
      { label: "Returns", value: (l) => l.filter((r) => r.sign < 0).length, format: "int" },
      {
        label: "Units sold",
        value: (l) => l.reduce((t, r) => t + r.lines.reduce((s, x) => s + r.sign * num(x.quantity), 0), 0),
        format: "int",
      },
      { label: "Tax", value: (l) => sumBy(l, "tax") },
      { label: "Net", value: (l) => sumBy(l, "net"), hero: true },
    ],
    []
  );

  return (
    <ReportScreen
      title="POS Summary Report"
      subtitle="POS sales less returns, by item, category, group and brand"
      unit="bills"
      rows={rows}
      tabs={tabs}
      filters={filters}
      kpis={kpis}
      chart={{ valueKey: "net", title: "Net POS sales" }}
      searchMatch={billSearch}
      searchPlaceholder="Invoice no, customer, item…"
      exportName="POS summary"
      reportNames={{
        item: "POS Item Summary",
        category: "POS Category Summary",
        group: "POS Group Summary",
        brand: "POS Brand Summary",
      }}
      onRefresh={refresh}
      onPeriodChange={load}
    />
  );
};

PosSummaryReport.propTypes = { branch: PropTypes.object };
